// src/pages/LoginPage.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';

const firebaseErrorMessage = (err, lang) => {
  const code = err?.code || '';
  const es = {
    'auth/invalid-email': 'El correo electrónico no es válido',
    'auth/user-not-found': 'No existe una cuenta con este correo',
    'auth/wrong-password': 'Contraseña incorrecta',
    'auth/invalid-credential': 'Correo o contraseña incorrectos',
    'auth/email-already-in-use': 'Este correo ya está registrado',
    'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
    'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde',
  };
  const en = {
    'auth/invalid-email': 'Invalid email address',
    'auth/user-not-found': 'No account found with this email',
    'auth/wrong-password': 'Incorrect password',
    'auth/invalid-credential': 'Invalid email or password',
    'auth/email-already-in-use': 'This email is already registered',
    'auth/weak-password': 'Password must be at least 6 characters',
    'auth/too-many-requests': 'Too many attempts. Try again later',
  };
  const map = lang === 'es' ? es : en;
  return map[code] || (lang === 'es' ? 'Ocurrió un error. Intenta de nuevo' : 'An error occurred. Try again');
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    }
    catch (err) { setError(firebaseErrorMessage(err, i18n.language)); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setError(t('login.enterEmail')); return; }
    setError(''); setResetMsg('');
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetMsg(t('login.resetSent'));
    } catch (err) { setError(firebaseErrorMessage(err, i18n.language)); }
  };

  const handleGoogle = async () => {
    setError('');
    try {
      const result = await loginWithGoogle();
      if (result?.needsPassword) {
        navigate('/set-password');
      } else {
        navigate('/dashboard');
      }
    }
    catch { setError(t('login.googleFailed')); }
  };

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 48, paddingBottom: 60 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>✓</div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: 4 }}>{t('login.welcomeBack')}</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.86rem' }}>{t('login.signInContinue')}</p>
        </div>
        <div className="card">
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">{t('login.email')}</label>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={t('login.emailPlaceholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('login.password')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder={t('login.passwordPlaceholder')}
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem',
                    padding: 4, lineHeight: 1,
                  }}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            {error && <p className="error-msg">{error}</p>}
            {resetMsg && <p style={{ color: 'var(--confirmed)', fontSize: '0.82rem', marginTop: 6 }}>{resetMsg}</p>}
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: 6 }}>
              {loading ? t('login.signingIn') : t('login.logIn')}
            </button>
            <button type="button" onClick={handleForgotPassword} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer', padding: '6px 0', fontFamily: 'inherit', width: '100%', textAlign: 'center' }}>
              {t('login.forgotPassword')}
            </button>
          </form>
          {!window.Capacitor?.isNativePlatform?.() && (
            <>
              <div className="or">{t('login.or')}</div>
              <button className="btn btn-secondary" onClick={handleGoogle}>
                <img src="https://www.google.com/favicon.ico" alt="G" style={{width:16,height:16}}/> {t('login.continueGoogle')}
              </button>
            </>
          )}
        </div>
        <p style={{ textAlign: 'center', marginTop: 14, fontSize: '0.86rem', color: 'var(--muted)' }}>
          {t('login.noAccount')}{' '}
          <Link to="/signup" style={{ color: 'var(--primary)', fontWeight: 600 }}>{t('login.signUpFree')}</Link>
        </p>
      </div>
    </div>
  );
}
